import { useEffect } from "react";
import {
  isContentFactor,
  standardFactor,
  unitFamily,
} from "@/modules/inventory/domain/lib/unit-conversion";
import { FormInput, FormSelect, RHF } from "@/shared/forms";
import { BASE_UNITS, RECEIVE_UNITS, SALE_UNITS } from "../api/client";

const STANDARD = new Set<string>(BASE_UNITS);
const DISCRETE = new Set(["pcs", "bag", "bottle"]);

function unitOptions(baseUnit: string, current: string) {
  const options: { value: string; label: string }[] = BASE_UNITS.filter(
    (unit) => standardFactor(baseUnit || "pcs", unit) != null,
  ).map((unit) => ({ value: unit, label: unit }));
  if (
    current &&
    !STANDARD.has(current) &&
    !options.some((o) => o.value === current)
  ) {
    options.push({ value: current, label: current });
  }
  return options;
}

function receiveUnitOptions(current: string) {
  const options: { value: string; label: string }[] = RECEIVE_UNITS.map(
    (unit) => ({ value: unit, label: unit }),
  );
  if (current && !options.some((option) => option.value === current)) {
    options.unshift({ value: current, label: current });
  }
  return options;
}

function saleUnitOptions(baseUnit: string, current: string) {
  const base = baseUnit.trim().toLowerCase();
  const options: { value: string; label: string }[] = DISCRETE.has(base)
    ? SALE_UNITS.map((unit) => ({ value: unit, label: unit }))
    : unitOptions(baseUnit, current);
  if (current && !options.some((option) => option.value === current)) {
    options.unshift({ value: current, label: current });
  }
  return options;
}

export function IngredientUnitFields() {
  const baseUnit = (RHF.useWatch({ name: "baseUnit" }) as string) ?? "pcs";
  const receiveUnit = (RHF.useWatch({ name: "receiveUnit" }) as string) ?? "";
  const receiveFactor = Number(RHF.useWatch({ name: "receiveToBase" }));
  const issueUnit = (RHF.useWatch({ name: "issueUnit" }) as string) ?? "";
  const issueFactor = Number(RHF.useWatch({ name: "issueToBase" }));
  const { setValue } = RHF.useFormContext();
  const issueIsContent = isContentFactor(baseUnit, issueUnit);

  useEffect(() => {
    syncStandardUnit(
      baseUnit,
      receiveUnit,
      receiveFactor,
      "receiveUnit",
      "receiveToBase",
      setValue,
    );
    syncIssueUnit(baseUnit, issueUnit, issueFactor, setValue);
  }, [baseUnit, receiveUnit, receiveFactor, issueUnit, issueFactor, setValue]);

  const receiveLocked = standardFactor(baseUnit, receiveUnit) != null;
  const issueLocked =
    !issueIsContent && standardFactor(baseUnit, issueUnit) != null;

  return (
    <div className="space-y-4 rounded-[12px] border bg-muted/20 p-4">
      <p className="font-medium text-sm">ຫນ່ວຍຮັບເຂົ້າ ແລະ ຫນ່ວຍຕັດຂາຍ</p>
      <p className="text-muted-foreground text-xs">
        ຄັງເກັບເປັນຫນ່ວຍພື້ນຖານ (pcs, bottle, bag). ຮັບເຂົ້າເປັນ pack ຫຼື box ໂດຍໃສ່ວ່າ 1
        ແພັກເທົ່າກັບຈັກຫນ່ວຍພື້ນຖານ. ຫນ່ວຍຕັດຂາຍເປັນ g ຫຼື ml.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormSelect
          name="receiveUnit"
          label="ຫນ່ວຍຮັບເຂົ້າ"
          options={receiveUnitOptions(receiveUnit)}
          requiredMark
        />
        <FormInput
          name="receiveToBase"
          label={factorLabel(receiveUnit, baseUnit, "ຫນ່ວຍຮັບ")}
          type="number"
          step="any"
          min={0}
          disabled={receiveLocked}
          requiredMark
        />
        <FormSelect
          name="issueUnit"
          label="ຫນ່ວຍຕັດຂາຍ"
          options={saleUnitOptions(baseUnit, issueUnit)}
          requiredMark
        />
        <FormInput
          name="issueToBase"
          label={
            issueIsContent
              ? `1 ${baseUnit || "ຫນ່ວຍພື້ນຖານ"} = ເທົ່າໃດ ${issueUnit}`
              : factorLabel(issueUnit, baseUnit, "ຫນ່ວຍຕັດ")
          }
          type="number"
          step="any"
          min={0}
          disabled={issueLocked}
          requiredMark
        />
      </div>
      <p className="text-sm">
        ຮັບ 1 {receiveUnit || "—"} ={" "}
        {Number.isFinite(receiveFactor) ? receiveFactor : "—"} {baseUnit} ໃນຄັງ
        {" · "}
        {issueIsContent ? (
          <>
            ຕັດ 1 {baseUnit || "—"} ={" "}
            {Number.isFinite(issueFactor) ? issueFactor : "—"} {issueUnit}
          </>
        ) : (
          <>
            ຕັດ 1 {issueUnit || "—"} ={" "}
            {Number.isFinite(issueFactor) ? issueFactor : "—"} {baseUnit}
          </>
        )}
      </p>
    </div>
  );
}

function factorLabel(unit: string, baseUnit: string, fallback: string): string {
  const selected = unit.trim() || fallback;
  const base = baseUnit.trim() || "ຫນ່ວຍພື້ນຖານ";
  return `1 ${selected} = ເທົ່າໃດ ${base}`;
}

function syncIssueUnit(
  baseUnit: string,
  unit: string,
  factor: number,
  setValue: (name: string, value: unknown) => void,
) {
  if (isContentFactor(baseUnit, unit)) return;
  syncStandardUnit(
    baseUnit,
    unit,
    factor,
    "issueUnit",
    "issueToBase",
    setValue,
  );
}

function syncStandardUnit(
  baseUnit: string,
  unit: string,
  factor: number,
  unitName: string,
  factorName: string,
  setValue: (name: string, value: unknown) => void,
) {
  if (!unit || unitFamily(unit) === "pack") {
    if (
      unit &&
      unit.trim().toLowerCase() === baseUnit.trim().toLowerCase() &&
      factor !== 1
    ) {
      setValue(factorName, 1);
    }
    return;
  }
  const fixed = standardFactor(baseUnit, unit);
  if (fixed == null) {
    setValue(unitName, baseUnit);
    setValue(factorName, 1);
    return;
  }
  if (factor !== fixed) setValue(factorName, fixed);
}
